import {
  withCompInfo,
  createComponentMapperModel,
  withStateRefsValues,
} from '@wix/editor-elements-integrations';
import type { WithInherit, Direction } from '@wix/editor-elements-common-utils';
import type {
  IMenuCarmiData,
  IMenuCssVars,
  IMenuDefinition,
  IMenuMapperProps,
  IMenuStateRefs,
  ItemTextAlignmentType,
  MenuAnimationNameType,
  MenuDisplayModeType,
  MenuJustificationType,
  MenuOrientationType,
  MenuOverflowType,
  MenuVerticalDropdownDisplayType,
} from '../Menu.types';
import {
  displayModeToCssVarsMap,
  orientationToCssVarsMap,
  dropdownDisplayTypeToCssVarMap,
  verticalOverflowCssVars,
  horizontalOverflowToCssVarsMap,
  justificationToCssVarsMap,
  itemTextAlignmentToCssVarsMap,
  animationNameToCssVarsMap,
  getDirectionCssVars,
} from './utils/MapperUtils';
import type { IMenuSDKData } from '@wix/editor-elements-corvid-utils/src/props-factories/menuUtils';
import {
  MenuOverflow,
  MenuOrientation,
  VerticalDropdownDisplay,
} from '../constants';
import { defaultTranslations } from './constants';

export const props = withCompInfo<
  IMenuMapperProps,
  IMenuDefinition,
  IMenuCarmiData
>()(['compData', 'translate'], ({ compData, translate }, carmiData) => {
  return {
    ...carmiData,
    ...compData,
    translations: {
      menuNavAriaLabel:
        translate(
          'ariaLabels',
          'dropDownMenu_AriaLabel_TopLevel_SiteNavigation',
        ) || defaultTranslations.menuNavAriaLabel,
      dropdownButtonAriaLabel:
        translate('ariaLabels', 'Menu_AriaLabel_Dropdown_Button') ||
        defaultTranslations.dropdownButtonAriaLabel,
    },
  };
});

export const css = withCompInfo<IMenuCssVars, IMenuDefinition>()(
  ['compData', 'styleProperties'],
  ({ compData, styleProperties }) => {
    const { direction } = compData;
    const {
      orientation = MenuOrientation.Horizontal,
      'vertical-dropdown-display':
        verticalDropdownDisplay = VerticalDropdownDisplay.AlwaysOpen,
      overflow = MenuOverflow.Scroll,
      'display-mode': displayMode,
      'menu-items-justification': justification = 'none',
      'item-text-align': itemTextAlign = 'center',
      'animation-name': animationName,
    } = styleProperties;

    const isVerticalOrientation = orientation === MenuOrientation.Vertical;
    return {
      ...getDirectionCssVars(direction as WithInherit<Direction>),
      ...displayModeToCssVarsMap[displayMode as MenuDisplayModeType],
      ...orientationToCssVarsMap[orientation as MenuOrientationType],
      ...justificationToCssVarsMap[justification as MenuJustificationType],
      ...itemTextAlignmentToCssVarsMap[itemTextAlign as ItemTextAlignmentType],
      ...animationNameToCssVarsMap[animationName as MenuAnimationNameType],
      ...(isVerticalOrientation
        ? {
            ...verticalOverflowCssVars,
            ...dropdownDisplayTypeToCssVarMap[
              verticalDropdownDisplay as MenuVerticalDropdownDisplayType
            ],
          }
        : horizontalOverflowToCssVarsMap[overflow as MenuOverflowType]),
    };
  },
);

export const sdkData = withCompInfo<IMenuSDKData, IMenuDefinition>()(
  ['pagesMap'],
  ({ pagesMap }) => {
    return {
      pageList: pagesMap,
    };
  },
);

export const stateRefs = withStateRefsValues<keyof IMenuStateRefs>([
  'currentUrl',
]);

export default createComponentMapperModel({
  css,
  props,
  stateRefs,
  sdkData,
});
